// The roster (spec §8). Airframe and hull numbers with sources; estimates are marked. photo = craft id.
import type { CraftSpec } from '../types';
import {
  lbToKg, galToKg, ktToMs, ftToM, AVGAS_KG_PER_L, JETA_KG_PER_L, DIESEL_KG_PER_L, GASOLINE_KG_PER_L,
} from '../physics/atmosphere';
import { SRC } from './sources';

/** estimate: 95 kg per passenger including bags, used for airliner payloads. */
const PAX_KG = 95;

export const CRAFT: CraftSpec[] = [
  {
    id: 'trainer-172',
    domain: 'air',
    className: 'Four-seat trainer',
    modelledOn: 'Cessna 172E (1964)',
    engineId: 'o-300-d',
    airframe: {
      emptyMassKg: lbToKg(1330), // standard empty weight, Skyhawk trim (PlanePhD 172E; 172 Guide 172F)
      payloadKg: lbToKg(2300 - 1330 - 36 * 6.01), // gross 2,300 lb (Wikipedia: from the 172D until the 172P) less empty weight and 36 usable gal at 6.01 lb/gal
      fuelCapacityKg: galToKg(36, AVGAS_KG_PER_L), // 39 US gal standard, 36 usable (172 Guide)
      wingAreaM2: 16.2, // 174 ft²
      aspectRatio: (11.02 * 11.02) / 16.2, // span 36 ft 2 in → 7.5
      oswald: 0.78, // estimate: plan envelope e 0.75–0.80
      cd0: 0.033, // estimate: plan envelope CD0 0.030–0.034
      vDesignMs: ktToMs(110), // estimate: fixed-pitch cruise propeller pitched for about 110 kt
      vStallMs: ktToMs(49), // estimate: 57 mph flaps-up in period brochures (PlanePhD lists 43 kt flaps down)
      ceilingM: ftToM(13100), // PlanePhD 172E
      engineCount: 1,
    },
    costCredits: 0,
    photo: 'trainer-172',
    defaultCruise: { altitudeFt: 7000, power: 0.65, mixture: 'peak' },
    sources: [SRC.wikiC172, SRC.guide172F, SRC.planephd172E, SRC.wikiO300, SRC.aopaCarson],
  },
  {
    id: 'fighter-p51',
    domain: 'air',
    className: 'WWII escort fighter',
    modelledOn: 'North American P-51D Mustang',
    engineId: 'merlin-v1650',
    airframe: {
      emptyMassKg: lbToKg(7635), // Wikipedia
      payloadKg: 100, // estimate: pilot, oxygen and ammunition
      fuelCapacityKg: galToKg(269, AVGAS_KG_PER_L), // 269 US gal internal (Wikipedia)
      wingAreaM2: 21.8, // 235 ft²
      aspectRatio: 5.83,
      oswald: 0.8, // estimate
      cd0: 0.0163, // Wikipedia: zero-lift drag coefficient 0.0163 (drag area 3.80 ft²)
      vDesignMs: ktToMs(250), // constant-speed propeller; unused
      vStallMs: ktToMs(87), // 100 mph (Wikipedia)
      ceilingM: ftToM(41900),
      engineCount: 1,
    },
    costCredits: 1500,
    photo: 'fighter-p51',
    // 2,000 rpm sits in the P-51 manual's 1,800–2,400 rpm economy-cruise band.
    defaultCruise: { altitudeFt: 10000, power: 0.55, mixture: 'peak', propRpm: 2000 },
    sources: [SRC.wikiP51, SRC.wikiDropTank],
  },
  {
    id: 'airliner-737',
    domain: 'air',
    className: 'Narrow-body airliner',
    modelledOn: 'Boeing 737-800',
    engineId: 'cfm56-7b26',
    airframe: {
      emptyMassKg: 41413, // operating empty weight (Wikipedia)
      payloadKg: 162 * PAX_KG, // estimate: 162 two-class seats × 95 kg
      fuelCapacityKg: galToKg(6875, JETA_KG_PER_L), // 6,875 US gal (26,020 L)
      wingAreaM2: 124.6, // 1,341 ft²
      aspectRatio: (34.32 * 34.32) / 124.6, // span 34.32 m without winglets (Wikipedia) → 9.45
      oswald: 0.8, // estimate
      cd0: 0.02, // estimate: typical narrow-body clean CD0 0.019–0.022, giving L/D max ≈ 17
      vDesignMs: ktToMs(452), // long-range cruise 452 kn at 39,000 ft (Wikipedia) → autothrottle Mach 0.79
      vStallMs: ktToMs(150), // estimate: clean 1-g stall at MTOW
      ceilingM: ftToM(41000),
      engineCount: 2,
    },
    costCredits: 3000,
    photo: 'airliner-737',
    defaultCruise: { altitudeFt: 35000, thrust: 0.8 },
    sources: [SRC.wiki737ng, SRC.wikiCfm56, SRC.leeham],
  },
  {
    id: 'multirole-f16',
    domain: 'air',
    className: 'Multirole fighter',
    modelledOn: 'F-16C Block 52',
    engineId: 'f100-pw-229',
    airframe: {
      emptyMassKg: 8573, // 18,900 lb (Wikipedia)
      payloadKg: 150, // estimate: pilot and gun ammunition
      fuelCapacityKg: lbToKg(7000), // 7,000 lb internal
      wingAreaM2: 27.87, // 300 ft²
      aspectRatio: (9.96 * 9.96) / 27.87, // span 9.96 m (Wikipedia) → 3.56
      oswald: 0.75, // estimate
      cd0: 0.018, // estimate: clean subsonic CD0, giving L/D max ≈ 10
      vDesignMs: ktToMs(500), // afterburning fighter: Mach limits come from the engine class, not from this field
      vStallMs: ktToMs(135), // estimate: clean stall at internal-fuel gross weight
      ceilingM: ftToM(50000),
      engineCount: 1,
    },
    costCredits: 3000,
    photo: 'multirole-f16',
    defaultCruise: { altitudeFt: 35000, thrust: 0.6, afterburner: false },
    sources: [SRC.wikiF16, SRC.wikiF100, SRC.wikiDropTank],
  },
  {
    id: 'sailboat-34',
    domain: 'sea',
    className: 'Cruising sailboat under power',
    modelledOn: '34 ft fin-keel production sloop with a Yanmar 2GM20 auxiliary (fitted to the CS 34 and Morgan 32)',
    engineId: 'yanmar-2gm20',
    hull: {
      hullType: 'displacement-small',
      lwlM: 9.1, // estimate: waterline length of a 34 ft sloop (about 29.9 ft)
      designSpeedKn: 6,
      designPowerKw: 6, // estimate: back-solved from a 3YM30 owner's 0.5 gal/h at 2,500 rpm and 6 kn (C-Brats) at 250 g/kWh; the 2GM20 is the 18 hp sibling
      hotelKw: 0.2, // estimate: instruments and autopilot
      fuelCapacityKg: galToKg(24, DIESEL_KG_PER_L), // 24 US gal (spec §8)
      minSpeedKn: 2,
      maxSpeedKn: 7.2, // hull speed 1.34·√(29.9 ft)
      engineCount: 1,
    },
    costCredits: 0,
    photo: 'sailboat-34',
    defaultCruise: { speedKn: 5.5 },
    sources: [SRC.wikiYanmar2gm20, SRC.cbratsYanmar, SRC.marineInsightHull],
  },
  {
    id: 'trawler-40',
    domain: 'sea',
    className: 'Coastal trawler',
    modelledOn: '40 ft full-displacement trawler (fuel curve anchored to the Nordhavn 41 sea trials)',
    engineId: 'volvo-md22',
    hull: {
      hullType: 'displacement-small',
      lwlM: 11, // estimate: about 36 ft waterline
      designSpeedKn: 7,
      designPowerKw: 28, // estimate: scaled from the Nordhavn 41's 1.66 gal/h at 6 kn and 4.1 gal/h at 8 kn to a 59 hp single
      hotelKw: 1, // estimate: refrigeration, instruments, autopilot
      fuelCapacityKg: galToKg(300, DIESEL_KG_PER_L), // 300 US gal (spec §8)
      minSpeedKn: 3,
      maxSpeedKn: 8, // hull speed 1.34·√(36 ft)
      engineCount: 1,
    },
    costCredits: 800,
    photo: 'trawler-40',
    defaultCruise: { speedKn: 6.5 },
    sources: [SRC.nordhavn41, SRC.volvoMd22p, SRC.manPrimer],
  },
  {
    id: 'console-26',
    domain: 'sea',
    className: 'Centre-console outboard boat',
    modelledOn: '26 ft single-outboard centre console (Yamaha F300 performance bulletins)',
    engineId: 'yamaha-f300',
    hull: {
      hullType: 'planing',
      lwlM: 7, // estimate
      designSpeedKn: 26, // estimate: about 30 mph at 4,000 rpm on plane
      designPowerKw: 95, // estimate: the Hurricane bulletin's 11.4 gal/h at 4,000 rpm is 106 kW at 0.30 kg/kWh, less the idle load below
      hotelKw: 11, // estimate: the 1.2 gal/h idle burn at 1,000 rpm expressed as a fixed load
      fuelCapacityKg: galToKg(150, GASOLINE_KG_PER_L), // 150 US gal (spec §8)
      minSpeedKn: 4,
      maxSpeedKn: 36, // estimate: the 2.5-exponent law meets the 300 hp rating here (bulletins show about 42 kn at 5,800 rpm)
      engineCount: 1,
    },
    costCredits: 1200,
    photo: 'console-26',
    defaultCruise: { speedKn: 24 },
    sources: [SRC.yamahaHurricane, SRC.yamahaAquaPatio, SRC.mercury279, SRC.volvoD6],
  },
  {
    id: 'utility-caravan',
    domain: 'air',
    className: 'Single-turboprop utility aircraft',
    modelledOn: 'Cessna 208 Caravan',
    engineId: 'pt6a-114a',
    airframe: {
      emptyMassKg: 2145, // 4,730 lb (Wikipedia)
      payloadKg: 3629 - 2145 - 1009, // MTOW 8,000 lb less empty and 2,224 lb of fuel
      fuelCapacityKg: galToKg(332, JETA_KG_PER_L), // 332 US gal
      wingAreaM2: 25.96, // 279 ft²
      aspectRatio: 9.702,
      oswald: 0.78, // estimate
      cd0: 0.035, // estimate: fixed gear and struts
      vDesignMs: ktToMs(180), // constant-speed propeller; unused
      vStallMs: ktToMs(61), // 61 KCAS (Wikipedia)
      ceilingM: ftToM(25000),
      engineCount: 1,
    },
    costCredits: 2000,
    photo: 'utility-caravan',
    defaultCruise: { altitudeFt: 10000, power: 0.75 },
    sources: [SRC.wikiC208, SRC.easaC208, SRC.wikiPt6],
  },
  {
    id: 'destroyer-ddg',
    domain: 'sea',
    className: 'Guided-missile destroyer',
    modelledOn: 'Arleigh Burke-class destroyer, Flight I',
    engineId: 'lm2500',
    hull: {
      hullType: 'displacement-small', // the ship notes compute n ≈ 3.05 for this hull from Schultz 2011
      lwlM: 142, // Schultz et al. 2011
      designSpeedKn: 20,
      designPowerKw: 14700, // computed in the ship notes: Schultz's clean-hull 6,100 kW at 15 kn scaled by (20/15)^3.05
      hotelKw: 2500, // estimate: one of the three 2,500 kW ship-service generators (Flight I) carrying the underway load
      fuelCapacityKg: 1.0e6, // estimate: back-solved from the published 4,400 nm at 20 kn (FAS, Wikipedia)
      minSpeedKn: 5,
      maxSpeedKn: 31,
      engineCount: 4,
    },
    costCredits: 3000,
    photo: 'destroyer-ddg',
    defaultCruise: { speedKn: 15 },
    sources: [SRC.wikiBurke, SRC.fasDdg51, SRC.schultz2011, SRC.geLm2500],
  },
  {
    id: 'container-triple-e',
    domain: 'sea',
    className: 'Ultra-large container ship',
    modelledOn: 'Emma Mærsk class (14RT-flex96C); the Triple-E 19 kn figure checks the curve',
    engineId: 'rt-flex96c',
    hull: {
      hullType: 'container-large',
      lwlM: 380, // estimate: 397 m overall
      designSpeedKn: 21, // estimate: the economical setting of 6,284 L/h sits at 45 % of 80 MW, i.e. 21 kn on the V^4 law from 25.5 kn
      designPowerKw: 36000, // estimate: 6,284 L/h × 0.98 kg/L ÷ 0.171 kg/kWh
      hotelKw: 4000, // estimate: reefer and ship-service load from the 20.8 MW of generators
      fuelCapacityKg: 8.0e6, // estimate: bunker capacity is not published on the pages consulted
      minSpeedKn: 8,
      maxSpeedKn: 25.5,
      engineCount: 1,
    },
    costCredits: 3000,
    photo: 'container-triple-e',
    defaultCruise: { speedKn: 16 },
    sources: [SRC.wikiEmma, SRC.endsTripleE, SRC.maerskEpa, SRC.manPrimer],
  },
  {
    id: 'twin-aisle-777',
    domain: 'air',
    className: 'Long-range twin-aisle airliner',
    modelledOn: 'Boeing 777-200LR',
    engineId: 'ge90-115b',
    airframe: {
      emptyMassKg: 145150, // 320,000 lb (Wikipedia)
      payloadKg: 301 * PAX_KG, // estimate: 301 three-class seats × 95 kg
      fuelCapacityKg: 162636, // 53,515 US gal with the three aft-hold auxiliary tanks (Wikipedia table)
      wingAreaM2: 436.8,
      aspectRatio: 9.61,
      oswald: 0.82, // estimate
      cd0: 0.018, // estimate: giving L/D max ≈ 18.5 (Wikipedia L/D table: 777-200 19.3)
      vDesignMs: ktToMs(484), // M 0.84 at 35,000 ft, the Mach the 777 wing was optimised for (Wikipedia)
      vStallMs: ktToMs(150), // estimate
      ceilingM: ftToM(43100),
      engineCount: 2,
    },
    costCredits: 3000,
    photo: 'twin-aisle-777',
    defaultCruise: { altitudeFt: 31000, thrust: 0.85 },
    sources: [SRC.wiki777, SRC.wikiGe90, SRC.wikiLd],
  },
];

export function craftById(id: string): CraftSpec | undefined {
  return CRAFT.find((c) => c.id === id);
}
